import React, { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  GHAPropertyBrand,
  GHAPropertyListAccountType,
  LEPropertyListAccountType,
  PropertyListMetadata,
  ScooponPropertyListAccountType,
  generatePropertyListXml,
  getPropertyListMetadata,
} from '~/services/ConnectionGoogleService';
import { formatDateWithClock, getBrowserTimeZoneAbbreviation } from '~/services/TimeService';

// Query key for property list metadata - shared for cache consistency
export const PROPERTY_LIST_METADATA_QUERY_KEY = 'propertyListMetadata';

interface Props {
  brand: GHAPropertyBrand;
}

// Map metadata to account type for generation
function getAccountTypeFromMetadata(metadata: PropertyListMetadata): GHAPropertyListAccountType {
  if (metadata.brand === 'scoopontravel') {
    return ScooponPropertyListAccountType.SCOOPON;
  }

  if (metadata.ghaPropertyType === 'vacation_rental') {
    return LEPropertyListAccountType.LE_VACATION_RENTAL;
  }

  return LEPropertyListAccountType.LPP;
}

// Map to display friendly names for property list types
function getPropertyListDisplayName(metadata: PropertyListMetadata): string {
  const { ghaPropertyType, brand } = metadata;

  if (brand === 'scoopontravel') {
    return 'Scoopon (Hotels)';
  }

  if (ghaPropertyType === 'vacation_rental') {
    return 'Vacation Rentals';
  }

  return 'LPP (Hotels)';
}

// Get hardcoded download URL for each account type
function getDownloadUrl(metadata: PropertyListMetadata): string {
  const baseUrl = window.configs.API_HOST;

  if (metadata.brand === 'scoopontravel') {
    return `${baseUrl}/api/connections/google/pull/scoopon/properties.xml.zip`;
  }

  if (metadata.ghaPropertyType === 'vacation_rental') {
    return `${baseUrl}/api/connections/google/pull/vacation-rental/properties.xml.zip`;
  }

  return `${baseUrl}/api/connections/google/pull/properties.xml.zip`;
}

interface PropertyListRow {
  key: string;
  displayName: string;
  updatedAt: string;
  propertyCount: number | null | undefined;
  downloadUrl: string;
  accountType: GHAPropertyListAccountType;
  refreshTooltip: string;
}

// The ALT account has no property_list row of its own by design: it shares the LPP
// catalogue, and its feed endpoint serves the same zip as the LPP list. Until the
// backend adds a discriminator, the ALT row is a frontend alias of the LPP row.
function buildPropertyListRows(metadata: Array<PropertyListMetadata>): Array<PropertyListRow> {
  return metadata.flatMap((item) => {
    const baseRow: PropertyListRow = {
      key: `${item.pricingMethod}-${item.ghaPropertyType}-${item.brand}`,
      displayName: getPropertyListDisplayName(item),
      updatedAt: item.updatedAt,
      propertyCount: item.propertyCount,
      downloadUrl: getDownloadUrl(item),
      accountType: getAccountTypeFromMetadata(item),
      refreshTooltip: 'Generate new property list',
    };

    if (baseRow.accountType !== LEPropertyListAccountType.LPP) {
      return [baseRow];
    }

    const altRow: PropertyListRow = {
      ...baseRow,
      key: `${baseRow.key}-alt`,
      displayName: 'ALT (Hotels) — shares LPP list',
      downloadUrl: `${window.configs.API_HOST}/api/connections/google/pull/alt/properties.xml.zip`,
      refreshTooltip: 'Regenerate the shared LPP/ALT property list',
    };
    return [baseRow, altRow];
  });
}

function formatPropertyCount(count: number | null | undefined): string {
  if (count === null || count === undefined) {
    return 'N/A';
  }
  return count.toLocaleString();
}

// Check if the last generation was less than 1 hour ago
function isRecentlyGenerated(updatedAt: string): boolean {
  const lastUpdate = new Date(updatedAt);
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
  return lastUpdate > oneHourAgo;
}

function GHAPropertyListMetadata({ brand }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // State for warning dialog
  const [showWarning, setShowWarning] = useState(false);
  const [pendingAccountType, setPendingAccountType] = useState<GHAPropertyListAccountType | null>(null);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: metadata = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [PROPERTY_LIST_METADATA_QUERY_KEY, brand],
    queryFn: async () => {
      const response = await getPropertyListMetadata({ brand });
      return response.result;
    },
    staleTime: 0, // Data is immediately considered stale
    refetchOnMount: 'always', // Always refetch when component mounts
  });

  const handleGenerateClick = (row: PropertyListRow) => {
    if (isRecentlyGenerated(row.updatedAt)) {
      setLastGeneratedAt(row.updatedAt);
      setPendingAccountType(row.accountType);
      setShowWarning(true);
    } else {
      handleSubmit(row.accountType);
    }
  };

  const handleSubmit = async (accountType: GHAPropertyListAccountType) => {
    setShowWarning(false);
    setIsSubmitting(true);

    try {
      await generatePropertyListXml(accountType);
      enqueueSnackbar('Property list XML generation has been queued successfully.', { variant: 'success' });
      // Invalidate and refetch metadata
      await queryClient.invalidateQueries({ queryKey: [PROPERTY_LIST_METADATA_QUERY_KEY, brand] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
      setPendingAccountType(null);
      setLastGeneratedAt(null);
    }
  };

  const handleWarningConfirm = () => {
    if (pendingAccountType) {
      handleSubmit(pendingAccountType);
    }
  };

  const handleWarningCancel = () => {
    setShowWarning(false);
    setPendingAccountType(null);
    setLastGeneratedAt(null);
  };

  if (isLoading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading property lists...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">Failed to load property list metadata</Alert>
      </Box>
    );
  }

  if (metadata.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          No property lists have been generated yet.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Account</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right"># Properties</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buildPropertyListRows(metadata).map((row) => {
              const formattedDate = `${formatDateWithClock(row.updatedAt)} (${getBrowserTimeZoneAbbreviation()})`;
              const propertyCount = formatPropertyCount(row.propertyCount);
              const isCurrentlySubmitting = isSubmitting && pendingAccountType === row.accountType;

              return (
                <TableRow key={row.key}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.displayName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formattedDate}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{propertyCount}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={row.refreshTooltip}>
                        <Box component="span">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleGenerateClick(row)}
                            disabled={isSubmitting}
                          >
                            {isCurrentlySubmitting ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                      </Tooltip>
                      <Tooltip title="Download property list ZIP">
                        <IconButton
                          size="small"
                          color="primary"
                          component="a"
                          href={row.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Warning Dialog */}
      <Dialog open={showWarning} onClose={handleWarningCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Recently Generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 0 }}>
            <Typography variant="body2" gutterBottom>
              <strong>This property list was recently generated.</strong>
            </Typography>
            <Typography variant="body2">
              Last generated at:{' '}
              {lastGeneratedAt
                ? `${formatDateWithClock(lastGeneratedAt)} (${getBrowserTimeZoneAbbreviation()})`
                : 'Unknown'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Are you sure you want to regenerate it?
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="text" onClick={handleWarningCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleWarningConfirm}
            disabled={isSubmitting}
            color="warning"
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isSubmitting ? 'Processing...' : 'Proceed Anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default GHAPropertyListMetadata;
